/**
 * Lado APP del puente builder. Corre dentro de la app del cliente (vía
 * veo-builder.js). Si el contexto trae `?veoBuilder=<token>`, se conecta con el
 * editor de Veo, atiende comandos y devuelve el elemento elegido.
 *
 * Dos transportes:
 *  - **iframe/popup** (`resolveTarget` = opener/parent): el dashboard es la
 *    ventana principal y embebe/abre la app; le hablamos a esa ventana.
 *  - **panel inyectado** (`ctx.panel`): la app es top-level (login real) y NOSOTROS
 *    inyectamos el panel del editor encima (iframe al dashboard); le hablamos a
 *    ese iframe. Es el modo sin adaptación por app.
 *
 * Seguridad: solo acepta mensajes cuyo `origin` coincida con el del dashboard
 * (`veoBuilderOrigin` / `document.referrer`) y cuyo `token` coincida.
 */
import {
  type BuilderClientContext,
  clearBuilderToken,
  resolveBuilderContext,
} from '../../core/builder-token';
import { hasDocument, hasWindow } from '../../utils/safe-env';
import { isDeviceAllowed } from '../guides/activation-devices';
import { closeGuidePreview, previewGuide } from '../guides/guide-preview';
import type { CompositeStep } from '../guides/types';
import { stepMatchesPage } from '../guides/url-match';
import { attachBlockDropTarget, type BlockDropTargetHandle } from './block-drop-target';
import {
  type BuilderCommand,
  type BuilderEvent,
  type PreviewStatus,
  VEO_BUILDER_SOURCE,
} from './bridge-protocol';
import { type BuilderChromeHandle, injectBuilderChrome } from './builder-chrome';
import { type BuilderPanelHandle, injectBuilderPanel } from './builder-panel';
import { attachDesignManipulator, type DesignManipulatorHandle } from './design-manipulator';
import { type ElementPickerHandle, startElementPicker } from './element-picker';

export interface BuilderModeHandle {
  /** Desconecta el puente y limpia picker/preview/panel. */
  teardown(): void;
}

/**
 * Inicializa el modo builder si hay contexto (URL o persistido). Devuelve un
 * handle, o `null` si no estamos en modo builder o no hay canal con el editor.
 * Es seguro llamarla siempre.
 */
export function initBuilderMode(): BuilderModeHandle | null {
  if (!hasWindow() || !hasDocument()) return null;

  // Contexto de la URL o de sessionStorage: sobrevive al redirect del guard de
  // auth (que suele tirar el query string) y a un login en la misma pestaña.
  const ctx = resolveBuilderContext();
  if (!ctx) return null;

  const token = ctx.token;
  const dashboardOrigin = resolveDashboardOrigin(ctx.origin);
  if (!dashboardOrigin) return null;

  let picker: ElementPickerHandle | null = null;
  let panel: BuilderPanelHandle | null = null;
  /** Barras del editor (topbar + pasos) sobre la app; solo en modo panel. */
  let chrome: BuilderChromeHandle | null = null;
  let staticTarget: Window | null = null;
  let manipulator: DesignManipulatorHandle | null = null;
  let dropTarget: BlockDropTargetHandle | null = null;
  /** Fantasma que sigue el cursor durante el drag de un bloque de la paleta. */
  let dragGhost: HTMLElement | null = null;
  /** Paso mostrado por la preview activa (destino de los drops de bloque). */
  let previewStepIndex = 0;
  /** Preview que llegó DURANTE un drag: se aplica al terminar el gesto. */
  let pendingPreview: Extract<BuilderCommand, { type: 'preview' }> | null = null;
  /** Último comando de preview: se re-evalúa al navegar (targeting por-página). */
  let lastPreview: Extract<BuilderCommand, { type: 'preview' }> | null = null;
  /** Modo vista previa (ojo de la topbar): guía sin editor ni barras. */
  let previewMode = false;
  /** Disparador por elemento objetivo armado para la preview (click/hover). */
  let previewTrigger: (() => void) | null = null;
  /** Ya se disparó: las siguientes previews se pintan directo hasta que se cierre. */
  let previewFired = false;
  /** «Retrasar la aparición» en curso para la preview (timer de delayMs). */
  let previewDelay: ReturnType<typeof setTimeout> | null = null;
  /** Si la tarjeta estaba visible al entrar en vista previa (se restaura al salir). */
  let panelWasVisible = true;
  const getTarget = (): Window | null => (panel ? panel.target() : staticTarget);

  const post = (event: BuilderEvent): void => {
    getTarget()?.postMessage({ source: VEO_BUILDER_SOURCE, token, ...event }, dashboardOrigin);
  };

  /**
   * ¿El paso que se está previsualizando aplica a la URL ACTUAL? El editor debe
   * reflejar el targeting por-página real: si el paso apunta a otra página, la
   * preview NO se muestra (igual que el runtime). `sitewide`/sin pageLocation →
   * siempre true, así el editado normal no se ve afectado.
   */
  const previewStep = (
    cmd: Extract<BuilderCommand, { type: 'preview' }>,
  ): CompositeStep | undefined => {
    const steps = cmd.guide.guideSteps as unknown as CompositeStep[] | undefined;
    return steps?.[cmd.guide.startStepIndex ?? 0];
  };
  const previewMatchesPage = (cmd: Extract<BuilderCommand, { type: 'preview' }>): boolean =>
    stepMatchesPage(previewStep(cmd)?.pageLocation, window.location.href);

  const postPreviewStatus = (payload: PreviewStatus): void =>
    post({ type: 'preview-status', payload });

  const withholdPreview = (): void => {
    manipulator?.detach();
    manipulator = null;
    dropTarget?.detach();
    dropTarget = null;
    closeGuidePreview();
  };

  /**
   * Aplica la preview respetando el targeting de página y (con `honorTrigger`)
   * el dispositivo; si no aplica, la cierra y le cuenta al panel por qué.
   */
  const applyPreviewGated = (cmd: Extract<BuilderCommand, { type: 'preview' }>): void => {
    lastPreview = cmd;
    if (!previewMatchesPage(cmd)) {
      cancelPreviewDelay();
      withholdPreview();
      postPreviewStatus({
        state: 'page-mismatch',
        pattern: previewStep(cmd)?.pageLocation?.pattern ?? '',
      });
      return;
    }
    const devices = cmd.guide.activationRules?.devices;
    if (cmd.honorTrigger && !previewMode && devices && !isDeviceAllowed(devices)) {
      cancelPreviewDelay();
      withholdPreview();
      postPreviewStatus({ state: 'device-mismatch', devices: devices as 'desktop' | 'mobile' });
      return;
    }
    applyPreview(cmd);
  };

  const disarmPreviewTrigger = (): void => {
    previewTrigger?.();
    previewTrigger = null;
  };

  const cancelPreviewDelay = (): void => {
    if (previewDelay) clearTimeout(previewDelay);
    previewDelay = null;
  };

  /**
   * Elemento objetivo: arma un listener delegado (click, y hover si aplica)
   * sobre `selector`; al disparar, pinta la preview. Ignora nuestros overlays.
   */
  const armPreviewTrigger = (selector: string, trigger: 'on_click' | 'on_hover'): void => {
    disarmPreviewTrigger();
    const onFire = (e: Event): void => {
      const target = e.target;
      if (!(target instanceof Element)) return;
      if (target.closest('[data-veo-builder-chrome],[data-veo-builder-panel],[data-veo-picker]')) {
        return;
      }
      try {
        if (!target.closest(selector)) return;
      } catch {
        return;
      }
      disarmPreviewTrigger();
      previewFired = true;
      if (lastPreview) applyPreviewGated(lastPreview);
    };
    document.addEventListener('click', onFire, true);
    if (trigger === 'on_hover') document.addEventListener('mouseover', onFire, true);
    previewTrigger = () => {
      document.removeEventListener('click', onFire, true);
      document.removeEventListener('mouseover', onFire, true);
    };
    postPreviewStatus({ state: 'waiting-trigger', trigger, selector });
  };

  /**
   * «Retrasar la aparición»: la preview espera `delayMs` como el runtime. Al
   * cumplirse se pinta y las siguientes previews (cada edición) van directo
   * hasta que el autor la cierre con ✕ — si no, cada tecla reiniciaría la espera.
   */
  const armPreviewDelay = (delayMs: number): void => {
    cancelPreviewDelay();
    previewDelay = setTimeout(() => {
      previewDelay = null;
      previewFired = true;
      if (lastPreview) applyPreviewGated(lastPreview);
    }, delayMs);
    postPreviewStatus({ state: 'waiting-delay', delayMs });
  };

  const applyPreview = (cmd: Extract<BuilderCommand, { type: 'preview' }>): void => {
    manipulator?.detach();
    manipulator = null;
    dropTarget?.detach();
    dropTarget = null;
    previewStepIndex = cmd.guide.startStepIndex ?? 0;

    // Elemento objetivo: hasta que el autor haga clic/hover en el elemento, la
    // preview no se pinta (como en el runtime). Tras cerrarla, se re-arma.
    const rules = cmd.guide.activationRules;
    const trig = rules?.trigger;
    const sel = rules?.selector?.trim();
    const gate = cmd.honorTrigger && !previewFired && !previewMode;
    if (gate && (trig === 'on_click' || trig === 'on_hover') && sel) {
      cancelPreviewDelay();
      closeGuidePreview();
      armPreviewTrigger(sel, trig);
      return;
    }
    const delayMs = trig === 'delayed' ? (rules?.delayMs ?? 0) : 0;
    if (gate && delayMs > 0) {
      disarmPreviewTrigger();
      closeGuidePreview();
      // Si ya hay una espera corriendo (el autor sigue editando) no se reinicia.
      if (!previewDelay) armPreviewDelay(delayMs);
      return;
    }
    disarmPreviewTrigger();
    cancelPreviewDelay();
    const handle = previewGuide({
      ...cmd.guide,
      onClosed: () => {
        // El autor cerró la guía con ✕ → vuelve a esperar la interacción/retraso.
        manipulator?.detach();
        manipulator = null;
        dropTarget?.detach();
        dropTarget = null;
        previewFired = false;
        if (lastPreview) applyPreviewGated(lastPreview);
      },
    });
    if (cmd.honorTrigger) postPreviewStatus({ state: 'shown' });
    // En vista previa la guía se ve como la vería el usuario: sin drag/resize.
    if (!cmd.editable || previewMode) return;
    void handle.ready.then((result) => {
      if (!result.rendered) return;
      const host = handle.host();
      if (!host) return;
      manipulator = attachDesignManipulator({
        host,
        guideType: cmd.guide.guideType,
        stepIndex: cmd.guide.startStepIndex ?? 0,
        onCommit: (payload) => post({ type: 'design-updated', payload }),
        onGestureEnd: () => {
          if (!pendingPreview) return;
          const queued = pendingPreview;
          pendingPreview = null;
          applyPreviewGated(queued);
        },
      });
      // Paleta → guía: soltar un bloque del panel sobre la preview lo inserta.
      dropTarget = attachBlockDropTarget({
        host,
        stepIndex: cmd.guide.startStepIndex ?? 0,
        onDrop: (payload) => post({ type: 'block-dropped', payload }),
      });
    });
  };

  const hideDragGhost = (): void => {
    dragGhost?.remove();
    dragGhost = null;
  };

  const moveDragGhost = (x: number, y: number, label: string): void => {
    if (!dragGhost) {
      dragGhost = document.createElement('div');
      dragGhost.style.cssText =
        'position:fixed;padding:4px 10px;border-radius:999px;background:#1c1917;color:#fff;' +
        'font:12px/1.4 system-ui,sans-serif;box-shadow:0 4px 14px rgba(0,0,0,0.35);' +
        'border:1px solid rgba(255,255,255,0.25);pointer-events:none;z-index:2147483647;';
      document.body.appendChild(dragGhost);
    }
    dragGhost.textContent = label;
    dragGhost.style.left = `${x + 14}px`;
    dragGhost.style.top = `${y + 12}px`;
  };

  /**
   * Drag de bloque por pointer: coords relativas al iframe del panel → página.
   * En pop-out no hay mapeo (otra ventana) — se ignora.
   */
  const handleBlockDrag = (cmd: Extract<BuilderCommand, { type: 'block-drag' }>): void => {
    const rect = panel?.frameRect();
    if (!rect || !dropTarget) {
      if (cmd.phase !== 'move') hideDragGhost();
      dropTarget?.cancelHover();
      return;
    }
    const x = rect.left + cmd.x;
    const y = rect.top + cmd.y;
    if (cmd.phase === 'move') {
      moveDragGhost(x, y, cmd.label || cmd.blockType);
      dropTarget.hoverAt(x, y);
      return;
    }
    hideDragGhost();
    if (cmd.phase === 'cancel') {
      dropTarget.cancelHover();
      return;
    }
    const spotAt = dropTarget.dropAt(x, y);
    if (spotAt) {
      post({
        type: 'block-dropped',
        payload: { stepIndex: previewStepIndex, blockType: cmd.blockType, ...spotAt },
      });
    }
  };

  const handleCommand = (cmd: BuilderCommand): void => {
    switch (cmd.type) {
      case 'panel-ready':
        // El panel cargó y ya escucha → recién ahora le mandamos `ready`. Con
        // `chrome: true` el panel sabe que las barras las pinta el SDK.
        post(chrome ? { type: 'ready', chrome: true } : { type: 'ready' });
        break;
      case 'editor-state':
        chrome?.setState(cmd.state);
        panel?.setTitle(
          cmd.state.section === 'activation'
            ? 'Ajustes de activación'
            : cmd.state.section === 'badge'
              ? 'Ajustes de badge'
              : cmd.state.section === 'target'
                ? 'Ajustes del elemento objetivo'
                : (cmd.state.steps[cmd.state.activeStep]?.label ?? 'Paso'),
        );
        break;
      case 'panel-visibility':
        panel?.setVisible(cmd.visible);
        break;
      case 'start-picker': {
        picker?.stop();
        // Para elegir con comodidad se esconde TODO (barras + tarjeta) mientras
        // dura el picker; vuelve como estaba al terminar (elegir o cancelar).
        const panelWasShown = panel?.isVisible() ?? false;
        if (panelWasShown) panel?.setVisible(false);
        chrome?.setHidden(true);
        // La guía en preview también estorba (tapa elementos): se cierra y se
        // re-pinta al terminar, con el mismo comando que la mostró.
        const previewToRestore = lastPreview;
        if (previewToRestore) {
          manipulator?.detach();
          manipulator = null;
          dropTarget?.detach();
          dropTarget = null;
          closeGuidePreview();
        }
        const restorePanel = (): void => {
          chrome?.setHidden(false);
          if (panelWasShown) panel?.setVisible(true);
          if (previewToRestore && lastPreview === previewToRestore) {
            applyPreviewGated(previewToRestore);
          }
        };
        picker = startElementPicker({
          onPick: (payload) => {
            picker = null;
            restorePanel();
            post({ type: 'picked', payload });
          },
          onCancel: () => {
            picker = null;
            restorePanel();
            post({ type: 'pick-cancelled' });
          },
          ...(cmd.maxAncestors !== undefined ? { maxAncestors: cmd.maxAncestors } : {}),
          ...(cmd.hint !== undefined ? { hint: cmd.hint } : {}),
        });
        break;
      }
      case 'stop-picker':
        picker?.stop();
        picker = null;
        chrome?.setHidden(false);
        break;
      case 'preview':
        if (!cmd.guide) break;
        // Mientras hay un drag en curso, no remontar la preview debajo del
        // mouse del autor: se encola el ÚLTIMO comando y se aplica al soltar.
        if (manipulator?.isDragging()) pendingPreview = cmd;
        else applyPreviewGated(cmd);
        break;
      case 'block-drag':
        handleBlockDrag(cmd);
        break;
      case 'close-preview':
        manipulator?.detach();
        manipulator = null;
        dropTarget?.detach();
        dropTarget = null;
        hideDragGhost();
        pendingPreview = null;
        lastPreview = null;
        disarmPreviewTrigger();
        cancelPreviewDelay();
        previewFired = false;
        closeGuidePreview();
        break;
      case 'teardown':
        teardown();
        break;
    }
  };

  const onMessage = (event: MessageEvent): void => {
    if (event.origin !== dashboardOrigin) return;
    const data = event.data as { source?: unknown; token?: unknown } | null;
    if (!data || data.source !== VEO_BUILDER_SOURCE || data.token !== token) return;
    handleCommand(data as BuilderCommand);
  };

  const onPageHide = (): void => post({ type: 'closed' });

  // ── Watcher de navegación (SPA): al cambiar de página, re-evalúa la preview
  // contra su targeting por-página (aparece/desaparece como en el runtime). ──
  const onNav = (): void => {
    if (lastPreview && !manipulator?.isDragging()) applyPreviewGated(lastPreview);
  };
  const origPush = window.history.pushState;
  const origReplace = window.history.replaceState;
  const installNavWatcher = (): void => {
    window.history.pushState = function pushState(this: History, ...args) {
      const r = origPush.apply(this, args as Parameters<History['pushState']>);
      onNav();
      return r;
    };
    window.history.replaceState = function replaceState(this: History, ...args) {
      const r = origReplace.apply(this, args as Parameters<History['replaceState']>);
      onNav();
      return r;
    };
    window.addEventListener('popstate', onNav);
    window.addEventListener('hashchange', onNav);
  };
  const removeNavWatcher = (): void => {
    window.history.pushState = origPush;
    window.history.replaceState = origReplace;
    window.removeEventListener('popstate', onNav);
    window.removeEventListener('hashchange', onNav);
  };

  const teardown = (): void => {
    window.removeEventListener('message', onMessage);
    window.removeEventListener('pagehide', onPageHide);
    removeNavWatcher();
    picker?.stop();
    picker = null;
    manipulator?.detach();
    manipulator = null;
    dropTarget?.detach();
    dropTarget = null;
    hideDragGhost();
    pendingPreview = null;
    disarmPreviewTrigger();
    cancelPreviewDelay();
    closeGuidePreview();
    post({ type: 'closed' }); // avisar al editor ANTES de sacar el panel
    panel?.teardown();
    panel = null;
    chrome?.teardown();
    chrome = null;
    clearBuilderToken(); // salir del modo builder: no re-activar en la próxima carga
  };

  // Transporte: panel inyectado (app top-level) o iframe/popup (embebida).
  if (ctx.panel) {
    // Chrome (barras) primero: el panel arranca debajo. Las acciones de las
    // barras van al panel como eventos `chrome-action`; seleccionar algo
    // vuelve a mostrar la tarjeta si estaba minimizada.
    chrome = injectBuilderChrome({
      appLabel: window.location.hostname,
      onAction: (action) => {
        switch (action.action) {
          // Controles que resuelve el SDK solo (no van al panel).
          case 'dock':
            panel?.setTopOffset(action.position === 'top' ? (chrome?.height() ?? 0) : 0);
            return;
          case 'show-panel':
            panel?.setVisible(true);
            return;
          case 'preview':
            if (previewMode) return;
            previewMode = true;
            panelWasVisible = panel?.isVisible() ?? true;
            panel?.setVisible(false);
            chrome?.setPreview(true);
            if (lastPreview) applyPreviewGated(lastPreview);
            return;
          case 'exit-preview':
            if (!previewMode) return;
            previewMode = false;
            chrome?.setPreview(false);
            panel?.setVisible(panelWasVisible);
            if (lastPreview) applyPreviewGated(lastPreview);
            return;
          case 'select-activation':
          case 'select-step':
          case 'add-step':
            panel?.setVisible(true);
            break;
        }
        post({ type: 'chrome-action', payload: action });
      },
    });
    panel = injectBuilderPanel({
      dashboardOrigin,
      token,
      appOrigin: window.location.origin,
      guide: ctx.guide,
      panelPath: ctx.panelPath,
      onClose: () => teardown(),
      topOffset: chrome.height(),
    });
    chrome.onHeightChange((h) => panel?.setTopOffset(chrome?.dock() === 'top' ? h : 0));
  } else {
    staticTarget = resolveTarget();
    if (!staticTarget) return null;
  }

  const activate = (): void => {
    window.addEventListener('message', onMessage);
    window.addEventListener('pagehide', onPageHide);
    installNavWatcher();
    // Panel: esperamos el `panel-ready` del iframe para postear `ready` (todavía
    // puede no tener su listener). iframe/popup: el opener ya escucha → posteamos ya.
    if (!ctx.panel) post({ type: 'ready' });
  };

  // Modo panel inyectado: la seguridad ya la da la sesión AUTENTICADA del
  // dashboard (el panel es un iframe del dashboard con Clerk) — el token es solo
  // correlación. Activamos directo, SIN verify: el verify contra el backend
  // podría fallar por expiración del token durante el login (que tarda), dejando
  // el panel para siempre en "conectando…".
  //
  // Modo iframe/popup (embebido): verificamos el token firmado antes de activar
  // (si `init()` dejó la config). Solo bloquea si el backend dice {valid:false}.
  const cfg = (window as unknown as { __veoBuilder?: { apiKey?: string; apiUrl?: string } })
    .__veoBuilder;
  if (!ctx.panel && cfg?.apiKey && cfg?.apiUrl) {
    void verifyBuilderToken(cfg.apiUrl, cfg.apiKey, token).then((ok) => {
      if (ok) activate();
      else if (window.console) console.warn('[veo] token de builder inválido — modo no activado');
    });
  } else {
    activate();
  }

  return { teardown };
}

/**
 * Verifica el token con el backend. Solo devuelve `false` (no activar) cuando el
 * backend responde explícitamente `{valid:false}` (token forjado/expirado/de
 * otro workspace). Ante 404/red caída/errores transitorios devuelve `true`
 * (best-effort) para no brickear el modo builder.
 */
async function verifyBuilderToken(apiUrl: string, apiKey: string, token: string): Promise<boolean> {
  try {
    const url = `${apiUrl.replace(/\/+$/, '')}/v1/builder/verify`;
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Api-Key': apiKey },
      body: JSON.stringify({ token }),
    });
    if (!res.ok) return true; // no pudimos verificar → best-effort
    const data = (await res.json().catch(() => null)) as { valid?: unknown } | null;
    return data?.valid !== false;
  } catch {
    return true; // red caída / sin backend → best-effort
  }
}

/** Ventana con la que hablar en modo iframe/popup: el opener o el parent. */
function resolveTarget(): Window | null {
  if (window.opener && window.opener !== window) return window.opener as Window;
  if (window.parent && window.parent !== window) return window.parent;
  return null;
}

function resolveDashboardOrigin(originHint: string | null): string | null {
  // Origen de la URL o persistido en el primer arranque (antes del redirect).
  if (originHint) {
    try {
      return new URL(originHint).origin;
    } catch {
      return null;
    }
  }
  // Fallback: el referrer. Poco fiable tras un redirect same-origin (apunta a la
  // propia app), por eso el origen persistido va primero.
  if (hasDocument() && document.referrer) {
    try {
      return new URL(document.referrer).origin;
    } catch {
      return null;
    }
  }
  return null;
}

// Tipo re-exportado para consumidores que tipan el contexto.
export type { BuilderClientContext };
